import { Inject, Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { Actions, Store, ofActionSuccessful } from '@ngxs/store';
import { Observable, isObservable, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { NotificationState, ShowRefreshNotification } from '../notification';
import { StorageService } from '../storage';
import { TranslationId } from '../transloco';
import { WINDOW } from '../window';
import { SimpleRouterNavigationExtras } from './model/simple-router-navigation-extras';
import { Navigate, RefreshWindow } from './simple-router.actions';

@Injectable({
  providedIn: 'root'
})
export class NavigationUtilityService {
  constructor(
    private actions$: Actions,
    private router: Router,
    private storageService: StorageService,
    private store: Store,
    @Inject(WINDOW) private window: Window
  ) {}

  private readonly REFRESH_MESSAGE_KEY: string = 'yattaRefreshMessage';

  navigateWithExtras(route: string, isLoading: boolean, addReferrer: boolean, extras?: SimpleRouterNavigationExtras): Observable<unknown> {
    const replaceUrl: boolean | undefined = isLoading && extras?.replaceUrl === undefined ? true : extras?.replaceUrl;
    const queryParams: Params = { referrer: addReferrer ? this.router.url : undefined, ...extras?.queryParams };
    return this.store.dispatch(new Navigate([route], { ...extras, replaceUrl, queryParams }));
  }

  handleRefreshWindow(performRefresh?: () => void | Observable<unknown>): Observable<unknown> {
    this.showRefreshMessageAndClearStorage().subscribe();

    return this.actions$.pipe(
      ofActionSuccessful(RefreshWindow),
      tap(({ message }: RefreshWindow) => this.storeRefreshMessage(message)),
      switchMap(() => this.performRefresh(performRefresh))
    );
  }

  private storeRefreshMessage(message?: TranslationId): void {
    if (message) {
      this.storageService.putInSessionStorage(this.REFRESH_MESSAGE_KEY, message);
    } else {
      this.storageService.removeFromSessionStorage(this.REFRESH_MESSAGE_KEY);
    }
  }

  private performRefresh(performRefresh?: () => void | Observable<unknown>): Observable<unknown> {
    if (performRefresh) {
      const refreshResult = performRefresh();
      if (isObservable(refreshResult)) {
        return refreshResult.pipe(switchMap(() => this.showRefreshMessageAndClearStorage()));
      }
    } else {
      this.window.location.reload();
    }
    return of(null);
  }

  private showRefreshMessageAndClearStorage(): Observable<unknown> {
    const refreshMessage = this.storageService.getAndRemoveFromSessionStorage<TranslationId>(this.REFRESH_MESSAGE_KEY);
    if (refreshMessage) {
      if (!this.store.selectSnapshot(NotificationState.hasNotifications)) {
        return this.store.dispatch(new ShowRefreshNotification(refreshMessage));
      }
    }
    return of(null);
  }
}
